import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchPublicCatalogListings, PublicCatalogListing } from '@/services/catalog';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { SearchInput } from '@/components/common/SearchInput';
import {
  Store,
  LogIn,
  Laptop as LaptopIcon,
  Package,
  ChevronDown,
  Loader2,
  PhoneCall,
  Cpu,
  Sparkles,
} from 'lucide-react';

function formatPriceRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return 'Call for Price';
  if (min != null && max != null && min === max) {
    return `Rs. ${min.toLocaleString('en-PK')}`;
  }
  if (min != null && max != null) {
    return `Rs. ${min.toLocaleString('en-PK')} – ${max.toLocaleString('en-PK')}`;
  }
  return `Rs. ${(min ?? max ?? 0).toLocaleString('en-PK')}`;
}

export const ShopCatalogPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [type, setType] = useState<'all' | 'laptop_group' | 'accessory'>('all');

  const { data: listings = [], isLoading, isError, error } = useQuery<PublicCatalogListing[]>({
    queryKey: ['public_catalog_listings'],
    queryFn: fetchPublicCatalogListings,
  });

  const filteredListings = useMemo(() => {
    return listings.filter((l) => {
      if (type !== 'all' && l.listing_type !== type) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const matchesTitle = l.title.toLowerCase().includes(s);
        const matchesBrand = l.brand ? l.brand.toLowerCase().includes(s) : false;
        const matchesModel = l.model ? l.model.toLowerCase().includes(s) : false;
        const matchesCpu = l.cpu ? l.cpu.toLowerCase().includes(s) : false;
        const matchesRams = l.ram_variants ? l.ram_variants.some((r) => r.toLowerCase().includes(s)) : false;
        const matchesStorages = l.storage_variants ? l.storage_variants.some((st) => st.toLowerCase().includes(s)) : false;
        const matchesGpus = l.gpu_variants ? l.gpu_variants.some((g) => g.toLowerCase().includes(s)) : false;
        return matchesTitle || matchesBrand || matchesModel || matchesCpu || matchesRams || matchesStorages || matchesGpus;
      }
      return true;
    });
  }, [listings, type, search]);

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col font-sans text-xs">
      {/* Header */}
      <header className="bg-white border-b border-brand-border sticky top-0 z-30 px-4 sm:px-8 py-3 flex items-center justify-between shadow-xs">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green font-extrabold text-base">
            AK
          </div>
          <div>
            <h1 className="font-bold text-sm text-brand-black tracking-tight uppercase">
              AL KUWAIT TECH
            </h1>
            <p className="text-[10px] text-brand-muted">Laptops, Computers & Accessories Store — Pakistan</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <a
            href="https://wa.me/923000000000?text=Hi%20Al%20Kuwait%20Tech%2C%20I%20am%20interested%20in%20browsing%20your%20inventory."
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 text-green-800 text-xs font-semibold rounded-md border border-green-200 hover:bg-green-100 transition-colors"
          >
            <PhoneCall className="w-3.5 h-3.5 text-green-600" />
            <span>WhatsApp Enquiry</span>
          </a>
          <Link
            to="/login"
            className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 bg-brand-green text-white text-xs font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-xs"
          >
            <LogIn className="w-3.5 h-3.5" />
            <span>Staff Portal</span>
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-white border-b border-brand-border py-8 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto text-center space-y-2">
          <div className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-brand-green/10 text-brand-green text-[11px] font-bold rounded-full mb-1">
            <Sparkles className="w-3 h-3" />
            <span>Official Product Showcase</span>
          </div>
          <h2 className="text-2xl font-extrabold text-brand-black tracking-tight">
            Certified Laptops & Shop Inventory
          </h2>
          <p className="text-brand-muted max-w-xl mx-auto text-xs">
            Verified laptops, ultrabooks, touchscreens, chargers and accessories. Immediate dispatch across Pakistan.
          </p>

          {/* Filters Bar */}
          <div className="pt-4 flex flex-col sm:flex-row gap-2 max-w-3xl mx-auto">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search brand, model, Core i7, 16GB, RTX, SSD, accessories..."
              />
            </div>

            <div className="relative">
              <select
                value={type}
                onChange={(e) => setType(e.target.value as any)}
                className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none font-medium text-xs"
              >
                <option value="all">All Categories</option>
                <option value="laptop_group">Laptops</option>
                <option value="accessory">Accessories & Parts</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <main className="flex-1 p-4 sm:p-8 max-w-6xl mx-auto w-full space-y-6">
        {isError && <ErrorAlert message={parseDatabaseError(error)} />}

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-2">
            <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            <p className="text-brand-muted font-medium">Loading store listings…</p>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {filteredListings.length === 0 ? (
              <div className="bg-white rounded-xl border border-brand-border p-12 text-center space-y-2">
                <Store className="w-12 h-12 text-brand-muted/30 mx-auto" />
                <p className="font-bold text-brand-black text-sm">No listings found</p>
                <p className="text-brand-muted">Try adjusting your search query or category filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredListings.map((listing) => (
                  <Link
                    key={listing.id}
                    to={`/shop/${listing.slug}`}
                    className="bg-white rounded-xl border border-brand-border hover:border-brand-green hover:shadow-md transition-all shadow-xs overflow-hidden flex flex-col justify-between group"
                  >
                    <div>
                      {/* Image Thumbnail */}
                      <div className="h-48 bg-brand-bg flex items-center justify-center p-3 border-b border-brand-border/60 relative">
                        {listing.cover_image_path ? (
                          <img
                            src={listing.cover_image_path}
                            alt={listing.title}
                            className="h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-200"
                          />
                        ) : (
                          <div className="text-brand-muted flex flex-col items-center">
                            {listing.listing_type === 'laptop_group' ? (
                              <LaptopIcon className="w-12 h-12 opacity-30 mb-1" />
                            ) : (
                              <Package className="w-12 h-12 opacity-30 mb-1" />
                            )}
                            <span className="text-[10px]">No image</span>
                          </div>
                        )}

                        {/* Type & Feature Badges */}
                        <div className="absolute top-2.5 left-2.5 flex items-center space-x-1 flex-wrap gap-y-1">
                          <span
                            className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase shadow-xs ${
                              listing.listing_type === 'laptop_group'
                                ? 'bg-brand-green text-white'
                                : 'bg-blue-600 text-white'
                            }`}
                          >
                            {listing.listing_type === 'laptop_group' ? 'Laptop' : 'Accessory'}
                          </span>
                          {listing.touchscreen === true && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[9px] font-bold rounded">
                              Touch
                            </span>
                          )}
                        </div>

                        {listing.is_featured && (
                          <span className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-purple-600 text-white rounded text-[9px] font-bold uppercase shadow-xs">
                            Featured
                          </span>
                        )}
                      </div>

                      {/* Card Content */}
                      <div className="p-4 space-y-2">
                        <h3 className="font-bold text-brand-black text-sm group-hover:text-brand-green transition-colors line-clamp-1">
                          {listing.title}
                        </h3>

                        {listing.cpu && (
                          <p className="text-[11px] text-brand-muted flex items-center space-x-1">
                            <Cpu className="w-3.5 h-3.5 text-brand-green" />
                            <span>{listing.cpu}</span>
                          </p>
                        )}

                        {/* Variants Snippet */}
                        {listing.ram_variants && listing.ram_variants.length > 0 && (
                          <div className="flex items-center space-x-1 flex-wrap gap-y-1 pt-1">
                            {listing.ram_variants.slice(0, 3).map((r, i) => (
                              <span
                                key={i}
                                className="px-1.5 py-0.2 bg-slate-100 text-slate-700 text-[9px] font-medium rounded"
                              >
                                {r}
                              </span>
                            ))}
                            {listing.storage_variants?.slice(0, 2).map((st, i) => (
                              <span
                                key={i}
                                className="px-1.5 py-0.2 bg-slate-100 text-slate-700 text-[9px] font-medium rounded"
                              >
                                {st}
                              </span>
                            ))}
                          </div>
                        )}

                        {listing.description && (
                          <p className="text-brand-muted text-[11px] line-clamp-2 pt-0.5">
                            {listing.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="p-4 pt-2 border-t border-brand-border/60 flex items-center justify-between">
                      <div>
                        <span className="text-sm font-extrabold text-brand-green block">
                          {formatPriceRange(listing.min_price, listing.max_price)}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          listing.available_quantity > 0
                            ? 'text-green-800 bg-green-50 border border-green-200'
                            : 'text-slate-600 bg-slate-100'
                        }`}
                      >
                        {listing.available_quantity > 0
                          ? `In Stock (${listing.available_quantity})`
                          : 'Available on Order'}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-brand-border p-6 text-center text-brand-muted space-y-1">
        <p className="font-bold text-brand-black">AL KUWAIT TECH — Retail & Repair Service Store</p>
        <p>© {new Date().getFullYear()} All Rights Reserved. WhatsApp Order Hotline: +92 300 0000000</p>
      </footer>
    </div>
  );
};
